import {clearAllBodyScrollLocks, enableBodyScroll} from 'body-scroll-lock';
import {Button, ButtonColor, ButtonSize, ButtonState, ButtonType} from 'components/atoms/LinksAndButtons/Button';
import {P} from 'components/atoms/Text/P';
import {SiteSpecificContent} from 'components/atoms/SiteSpecificContent';
import {HorizontalUserCard} from 'components/molecules/MyPagesComponents/HorizontalUserCard';
import {VerticalResidenceCard} from 'components/molecules/ResidentialCards/VerticalResidenceCard';
import {VipPeriodTermsModal} from 'components/modals/General/_termsModal';
import {SITE_URLS} from 'features/search/constants';
import {LOCALE, SITE, HeadlineLevel, HeadlineFont, TextColor } from 'features/ui/enums';
import * as React from 'react';
import {useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {sanitizeHtml} from 'features/services/utils/sanitize-html';
import {StageStatus} from 'features/search/filters/enums';
import {gtmEventsPurchaseSignupSubmit} from 'features/services/googleTagManager/events';
import {getCurrentLocale, getCurrentSite} from 'features/services/utils/env-helper';
import {getObjectImagesByType} from 'features/services/utils/image-helpers';
import {getFormattedMessage} from 'features/services/utils/language-helper';

import {ObjectImageType} from 'features/template/viewModels';

import './style.scss';
import {useModal} from "features/modal/hooks/useModal";
import {SearchResult} from "features/search/types";
import {ResidenceObjectViewModel} from "features/template/viewModels/ResidenceObject";

import {useAuth} from "features/authentication/hooks/useAuth";
import {useAddPurchase} from "features/authentication/services/PurchaseService/mutation";
import {PurchaseService} from "features/authentication/services/PurchaseService/service";
import {useSearchParams} from "react-router";
import {Loader} from "@atoms/Loaders/Loader";
import {Headline} from '@atoms/Text/Headline';

enum VipWindowState {
    ObjectView,
    AcceptView,
    IframeView,
    DoneView
}

interface Props {
    residence: ResidenceObjectViewModel;
    stageUrl: string;
    purchaseSignupTermsText?: string;
    vipPurchaseSignupModalText?: string;
    standAlone?: boolean;
}

export const VipPeriodWindow: React.FunctionComponent<Props> = ({
                                                                    residence, stageUrl,
                                                                    standAlone,
                                                                    purchaseSignupTermsText,
                                                                    vipPurchaseSignupModalText,
                                                                }) => {

    const { open, close } = useModal();

    const {user} = useAuth();

    const [loading, setLoading] = useState<boolean>(false);
    const [currentView, setCurrentView] = useState<VipWindowState>(user?.hasPurchase(residence.objectId) ? VipWindowState.DoneView : VipWindowState.ObjectView);
    const floorplan = getObjectImagesByType(residence.images, ObjectImageType.FloorPlan);
    const currentSite = getCurrentSite();

    const {mutate: addPurchase, isPending, isSuccess} = useAddPurchase();

    const [searchParams, setSearchParams] = useSearchParams();
    const cid = searchParams.get('transaction_id');

    useEffect(() => {
        doRedirectCallback();
    }, [cid, user]);

    // TODO: Needs some backend changes to integrate into identification flow
    const doRedirectCallback = async () => {
        if(!user) return;

        if(cid) {
            setSearchParams({});

            setLoading(true);

            if(user.hasPurchase(residence.objectId)) {
                setLoading(false);
                setCurrentView(VipWindowState.DoneView);
                return;
            }

            addPurchase({
                stageId: residence.stage,
                objectId: residence.objectId,
            });

            setCurrentView(VipWindowState.DoneView);
            setLoading(false);
        }
    };

    useEffect(() => {
        if(isSuccess) {
            gtmEventsPurchaseSignupSubmit()();
            setCurrentView(VipWindowState.DoneView);
        }
    }, [isSuccess]);

    const onSend = async () => {
        setLoading(true);

        if(!user || user.hasPurchase(residence.objectId)) {
            setLoading(false);
            return;
        }

        addPurchase({
            stageId: residence.stage,
            objectId: residence.objectId,
        });

        setLoading(false);

    };

    const getNumberOfPurchasesMade = () => {
        if(!user) return 0;

        const totalMade = user.purchases.filter(x => x.stageId === residence.stage);
        if (!totalMade) return 1;
        return totalMade.length;
    };

    const closeModal = () => {
        clearAllBodyScrollLocks();
        if (!standAlone) close();
        enableBodyScroll();

        if (!standAlone) {
            if (document.getElementsByTagName('html') !== null && document.getElementsByTagName('html').item(0) !== null) {
                //@ts-expect-error
                document.getElementsByTagName('html').item(0).classList.remove('blur-open')
            }
        }
    };

    const onNetsPurchaseSignClick = async () => {
        setLoading(true);

        if (standAlone) {
            window.location.hash = '';
            open(renderAcceptView(), { fullscreen: false, ariaLabel: getFormattedMessage('jm.components.VipPeriodWindow.ariaLabel') });

            return;
        }
        setCurrentView(VipWindowState.AcceptView);
        setLoading(false);
    };

    const acceptTerms = async () => {
        setLoading(true);
        const response = await PurchaseService.addPurchaseEidentStart({
            objectId: residence.objectId,
            stageId: residence.stage,
        });
        setLoading(false);

        window.location.href = response;
    };

    useEffect(() => {
        if (!standAlone) {
            clearAllBodyScrollLocks();
            enableBodyScroll();
        }
    }, []);

    const renderAcceptView = () => {
        return (
            <VipPeriodTermsModal
                acceptTerms={acceptTerms}
                purchaseSignupTermsText={purchaseSignupTermsText}
            />
        );
    };

    const getResidenceName = (residenceName: string) => {
        return residenceName && residenceName != null ?
            residenceName.toUpperCase() :
            residenceName;
    };

    if(loading) return <div className="vip-period-window">
        <Loader fullscreen={false}/>
    </div>

    return (
        <div className="vip-period-window">
            {currentView === VipWindowState.ObjectView &&
                <React.Fragment>
                    <Headline level={HeadlineLevel.H2}>
                        <SiteSpecificContent notNorway>
                            <FormattedMessage id="jm.components.VipPeriodWindow.purchaseFor"
                                              values={{objectNumber: getResidenceName(residence.name)}}/>
                        </SiteSpecificContent>

                        <SiteSpecificContent showOnNorway>
                            <FormattedMessage id="jm.components.VipPeriodWindow.purchaseFor"
                                              values={{objectNumber: getResidenceName(residence.name)}}/>
                        </SiteSpecificContent>
                    </Headline>

                    <div
                        className="vip-period-window__description"
                        dangerouslySetInnerHTML={{__html: sanitizeHtml(vipPurchaseSignupModalText ? vipPurchaseSignupModalText : '')}}
                    />

                    {getCurrentSite() === SITE.NO &&
                        <Headline level={HeadlineLevel.H5} 
                        className="vip-period-window__binding-title">
                            <FormattedMessage id="jm.components.VipPeriodWindow.bindingTerm"/>
                        </Headline>
                    }

                    <VerticalResidenceCard
                        residence={(residence as unknown) as SearchResult}
                        image={floorplan && floorplan.length > 0 ? floorplan[0].url : ''}
                        hideArrow
                        forceVisibility
                    />
                    <HorizontalUserCard />

                    <div className="vip-period-window__button-container">
                        {(currentSite === SITE.SV || currentSite === SITE.BORATT || currentSite === SITE.SENIORGARDEN) &&
                            <Button
                                onClick={onSend}
                                buttonState={isPending
                                    ? ButtonState.LOADING
                                    : ButtonState.DEFAULT
                                }
                                buttonColor={(currentSite === SITE.BORATT || currentSite === SITE.SENIORGARDEN) ? ButtonColor.CTA : ButtonColor.PURCHASE}
                                buttonSize={ButtonSize.DEFAULT}
                                buttonType={ButtonType.DEFAULT}
                            >
                                <FormattedMessage id="jm.components.VipPeriodWindow.send"/>
                            </Button>
                        }
                        {currentSite === SITE.NO &&
                            <Button
                                onClick={onNetsPurchaseSignClick}
                                data-test-id="vip-purchase-next-step"
                                buttonState={loading
                                    ? ButtonState.LOADING
                                    : ButtonState.DEFAULT
                                }
                                buttonColor={ButtonColor.CTA}
                                buttonSize={ButtonSize.DEFAULT}
                                buttonType={ButtonType.DEFAULT}
                            >
                                Neste trinn
                            </Button>
                        }
                    </div>

                    <P className="vip-period-window__fine-print" center color={TextColor.MUTED}>
                        <FormattedMessage id="jm.components.VipPeriodWindow.purchaseInfo"/>
                    </P>
                </React.Fragment>
            }

            {currentView === VipWindowState.AcceptView && renderAcceptView()}

            {currentView === VipWindowState.DoneView &&
                <div className="vip-period-window__done-view">
                    <Headline level={HeadlineLevel.H3}>
                        <FormattedMessage id="jm.components.VipPeriodWindow.thankYouHeader"
                                          values={{name: getResidenceName(residence.name)}}/>
                    </Headline>
                    <P>
                        <FormattedMessage id="jm.components.VipPeriodWindow.thankYouDescription"/>
                    </P>

                    <div
                        className={`vip-period-window__done-buttons ${getCurrentLocale() !== LOCALE.NO ? '' : 'vip-period-window__done-buttons--no'}`}>
                        <Button
                            href={`${SITE_URLS.myResidences}#purchases`}
                            onClick={closeModal}
                            data-test-id="vip-purchase-my-residences"
                            buttonColor={ButtonColor.DEFAULT}
                            buttonSize={ButtonSize.WIDE}
                            buttonType={ButtonType.GHOST}
                        >
                            <FormattedMessage
                                id="jm.components.VipPeriodWindow.myPurchases"/> ({getNumberOfPurchasesMade()})
                        </Button>
                        {residence.stageStatus === StageStatus.VIP &&
                            <Button
                                href={`${stageUrl}#objects`}
                                onClick={closeModal}
                                data-test-id="vip-purchase-more"
                                buttonColor={ButtonColor.CTA}
                                buttonSize={ButtonSize.WIDE}
                                buttonType={ButtonType.DEFAULT}
                            >
                                <FormattedMessage id="jm.components.VipPeriodWindow.purchaseMore"/>
                            </Button>
                        }
                    </div>
                </div>
            }
        </div>
    )
};
